import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { AuditResponse } from '@/lib/types';

/**
 * The audit trail, read-only.
 *
 * There is no write here on purpose: a record the interface can amend answers
 * nothing. Retention is a scheduled delete on the server.
 */

interface AuditParams extends Record<string, string | number | undefined> {
  page?: number;
  limit?: number;
  action?: string;
  result?: 'success' | 'failure';
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export function useAudit(params: AuditParams = {}) {
  return useQuery({
    queryKey: ['audit', params],
    queryFn: () => apiFetch<AuditResponse>(`/v1/admin/audit${toQuery(params)}`),
    // Append-only, so a stale page is never wrong — only behind.
    staleTime: 30 * 1000,
  });
}

export function useAuditActions() {
  return useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: async () => {
      const response = await apiFetch<{ actions: string[] }>('/v1/admin/audit/actions');
      return response.actions;
    },
    staleTime: 60 * 60 * 1000,
  });
}
